/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

/* istanbul ignore file */
// NOTE!
// This file is being ignored in the test coverage report for now. It reports `0%` functions
// tested, which brings down the overall coverage and blocks CI. There are tests still, but
// we don't want it to count toward coverage until we figure out how to cover the `functions`
// metric for this file in its test.

import React from 'react'
import {useEffect} from 'react'

import loadable from '@loadable/component'
import {Redirect, withRouter} from 'react-router-dom'
import {getConfig} from 'pwa-kit-runtime/utils/ssr-config'

// Components
import {Skeleton} from '@chakra-ui/react'
import {configureRoutes} from './utils/routes-utils'
import useMultiSite from './hooks/use-multi-site'
import {HOME_HREF} from './constants'

const fallback = <Skeleton height="75vh" width="100%" />

// Pages
const Home = loadable(() => import('./pages/home'), {fallback})
const Login = loadable(() => import('./pages/login'), {fallback})
const Registration = loadable(() => import('./pages/registration'), {fallback})
const ResetPassword = loadable(() => import('./pages/reset-password'), {fallback})
const Cart = loadable(() => import('./pages/cart'), {fallback})
const Checkout = loadable(() => import('./pages/checkout'), {fallback})
const CheckoutConfirmation = loadable(() => import('./pages/checkout/confirmation'), {fallback})
const LoginRedirect = loadable(() => import('./pages/login-redirect'), {fallback})
const MyStokke = loadable(() => import('./pages/my-stokke'), {fallback})
const ProductDetail = loadable(() => import('./pages/product-detail'), {fallback})
const ProductList = loadable(() => import('./pages/product-list'), {fallback})
const CountrySelector = loadable(() => import('./pages/country-selector'))
const PageNotFound = loadable(() => import('./pages/page-not-found'))
const Content = loadable(() => import('./pages/content'), {fallback})
const CheckoutCancellation = loadable(() => import('./pages/checkout/cancellation'), {fallback})
const ShipmentTracking = loadable(() => import('./pages/shipment-tracking'), {fallback})
const ReturnOrder = loadable(() => import('./pages/return-order'), {fallback})

const RedirectToHome = () => {
    const {buildUrl} = useMultiSite()
    return <Redirect to={buildUrl(HOME_HREF)} />
}

const routes = [
    {
        path: '/',
        component: RedirectToHome,
        exact: true
    },
    {
        path: '/home',
        component: Home,
        exact: true
    },
    {
        path: '/login',
        component: Login,
        exact: true
    },
    {
        path: '/registration',
        component: Registration,
        exact: true
    },
    {
        path: '/reset-password',
        component: ResetPassword,
        exact: true
    },
    {
        path: '/my-stokke',
        component: MyStokke
    },
    {
        path: '/appcheckout',
        component: Checkout,
        exact: true
    },
    {
        path: '/appcheckout/confirmation',
        component: CheckoutConfirmation,
        exact: true
    },
    {
        path: '/callback',
        component: LoginRedirect,
        exact: true
    },
    {
        path: '/appcart',
        component: Cart,
        exact: true
    },
    {
        path: '/(stokke|ct|newsroom|customer-service)/:contentId.html',
        component: Content
    },
    {
        path: '/(stokke|ct|newsroom|customer-service)/*/:contentId.html',
        component: Content
    },
    {
        // legacy PWA content urls
        path: '/content/:contentId',
        component: Content
    },
    {
        path: '/search',
        component: ProductList
    },
    {
        path: '/category/:categoryPath+',
        component: ProductList
    },
    {
        path: '/country-selector',
        component: CountrySelector
    },
    {
        path: '/cancel-order/:hash',
        component: CheckoutCancellation
    },
    {
        path: '/*/:productId.html',
        component: ProductDetail
    },
    // legacy product URLs
    {
        path: '/product/:productId',
        component: ProductDetail
    },
    {
        path: '/shipment-tracking',
        component: ShipmentTracking
    },
    {
        path: '/return-order',
        component: ReturnOrder
    },
    {
        path: '*',
        component: withRouter((props) => {
            const {location} = props
            const urlParams = new URLSearchParams(location.search)

            useEffect(() => {
                const newURL = new URL(window.location)
                if (!urlParams.has('redirected')) {
                    newURL.searchParams.append('redirected', '1')
                    window.location.href = newURL
                }
            })
            if (urlParams.has('redirected')) {
                return <PageNotFound {...props} />
            }
            return null
        })
    }
]

export default () => {
    const config = getConfig()
    return configureRoutes(routes, config, {
        ignoredRoutes: ['/callback', '*']
    })
}
